import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr, isPaywall } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { Container, PageHeader, Spinner, PaywallNotice } from "@/components/shared";
import { toast } from "sonner";
import {
  Frown, Shield, AlertTriangle, HelpCircle, Home, MessageCircle, Hand, GraduationCap,
  Users, Landmark, Scale, Sparkles, Heart, UserCheck, FileText, Phone, HeartHandshake,
  FolderOpen, Camera, Mail, Building2, BookOpen, ArrowLeft, LifeBuoy, X, Send,
} from "lucide-react";

const IC = { Frown, Shield, AlertTriangle, HelpCircle, Home, MessageCircle, Hand, GraduationCap,
  Users, Landmark, Scale, Sparkles, Heart, UserCheck, FileText, Phone, HeartHandshake,
  FolderOpen, Camera, Mail, Building2, BookOpen };

export default function YouthSpace({ space }) {
  const { user } = useAuth();
  const isJeunes = space === "jeunes";
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");
  const [selected, setSelected] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/youth", { params: { space } }); setData(data); }
    catch (e) { toast.error(apiErr(e)); }
  }, [space]);
  useEffect(() => { setData(null); setCat(""); setSelected(null); load(); }, [load]);

  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true); setPaywall(false); setAnswer(null);
    try { const { data } = await api.post("/ai/youth-assist", { question: q, space }); setAnswer(data); }
    catch (e) { if (isPaywall(e)) setPaywall(true); else toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <Container><Spinner label="Chargement..." /></Container>;
  const hero = isJeunes ? data.images.classroom : data.images.reassure;
  const items = cat ? data.items.filter((i) => i.category === cat) : data.items;

  if (selected) {
    const Icon = IC[selected.icon] || BookOpen;
    return (
      <Container>
        <button data-testid="youth-back" onClick={() => setSelected(null)} className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-2 mb-6"><ArrowLeft size={16} /> Retour</button>
        <div className="w-12 h-12 rounded-2xl bg-gold/12 border border-gold/25 flex items-center justify-center mb-4"><Icon size={24} className="text-gold" strokeWidth={1.5} /></div>
        <p className="text-gold text-xs uppercase tracking-[0.2em] mb-2">{selected.category}</p>
        <h1 className="font-display text-2xl md:text-3xl mb-4" data-testid="youth-detail-title">{selected.title}</h1>
        <div className="card-surface p-6"><p className="text-white/85 leading-relaxed whitespace-pre-wrap">{selected.body}</p></div>
        {isJeunes && (
          <button data-testid="youth-help-open-detail" onClick={() => setShowHelp(true)} className="mt-5 w-full gold-gradient text-black rounded-2xl px-6 py-4 font-medium inline-flex items-center justify-center gap-2 text-lg">
            <LifeBuoy size={22} /> J'ai besoin d'aide
          </button>
        )}
        {showHelp && <HelpModal actions={data.help_actions} onClose={() => setShowHelp(false)} />}
      </Container>
    );
  }

  return (
    <Container>
      <div className="relative rounded-3xl overflow-hidden mb-6 fade-up">
        <img src={hero} alt="" className="w-full h-48 md:h-64 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 p-5 md:p-7">
          <p className="text-gold text-xs uppercase tracking-[0.2em] mb-1">{isJeunes ? "Espace Jeunes" : "Espace Parents"}</p>
          <h1 className="font-display text-2xl md:text-4xl font-light">{isJeunes ? "Tu n'es pas seul·e" : "Accompagner votre enfant"}</h1>
        </div>
      </div>

      {isJeunes && (
        <button data-testid="youth-help-button" onClick={() => setShowHelp(true)}
          className="w-full mb-6 gold-gradient text-black rounded-2xl px-6 py-4 font-semibold inline-flex items-center justify-center gap-2 text-lg hover:opacity-90 transition-opacity shadow-lg shadow-gold/10">
          <LifeBuoy size={24} /> J'ai besoin d'aide
        </button>
      )}

      {/* Assistant adaptatif */}
      <div className="card-surface p-5 mb-6">
        <div className="flex items-center gap-2 mb-2"><Sparkles size={18} className="text-gold" /><h3 className="font-display text-lg">{isJeunes ? "Pose ta question" : "Posez votre question"}</h3></div>
        <p className="text-white/45 text-sm mb-3">{isJeunes ? "Explique ce qui t'arrive, avec tes mots. La réponse est adaptée à toi." : "Décrivez la situation de votre enfant : réponse pratique et étapes concrètes."}</p>
        <div className="flex gap-2">
          <input data-testid="youth-assist-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder={isJeunes ? "Ex : on se moque de moi en classe…" : "Ex : mon enfant subit des moqueries racistes…"}
            className="flex-1 bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm" />
          <MicButton testid="youth-assist-mic" value={q} onChange={setQ} />
          <button data-testid="youth-assist-button" onClick={ask} disabled={busy || !q.trim()} className="gold-gradient text-black rounded-xl px-4 text-sm font-medium inline-flex items-center gap-1.5"><Send size={15} /></button>
        </div>
        {busy && <Spinner label="Je réfléchis..." />}
        {paywall && <PaywallNotice />}
        {answer && (
          <div className="mt-4 space-y-3" data-testid="youth-assist-result">
            <p className="text-white/90 text-sm leading-relaxed whitespace-pre-wrap">{answer.reponse}</p>
            {(answer.etapes || []).length > 0 && (
              <div><p className="text-gold text-xs uppercase tracking-wide mb-1">Ce que tu peux faire</p>
                <ul className="space-y-1">{answer.etapes.map((s, i) => <li key={i} className="text-white/80 text-sm flex gap-2"><span className="text-gold/60">—</span>{s}</li>)}</ul></div>
            )}
            {(answer.contacts || []).length > 0 && (
              <div><p className="text-gold text-xs uppercase tracking-wide mb-1">Qui contacter</p>
                <ul className="space-y-1">{answer.contacts.map((s, i) => <li key={i} className="text-white/80 text-sm flex gap-2"><span className="text-gold/60">—</span>{s}</li>)}</ul></div>
            )}
            {answer.disclaimer && <p className="text-white/35 text-xs pt-1">{answer.disclaimer}</p>}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setCat("")} className={`text-sm rounded-full px-3.5 py-1.5 border ${!cat ? "border-gold/50 bg-gold/10 text-gold" : "border-white/12 text-white/60"}`}>Tout</button>
        {data.categories.map((c) => (
          <button key={c} data-testid={`youth-cat-${c}`} onClick={() => setCat(cat === c ? "" : c)} className={`text-sm rounded-full px-3.5 py-1.5 border ${cat === c ? "border-gold/50 bg-gold/10 text-gold" : "border-white/12 text-white/60"}`}>{c}</button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => {
          const Icon = IC[it.icon] || BookOpen;
          return (
            <button key={it.content_id} data-testid={`youth-card-${it.content_id}`} onClick={() => { setSelected(it); window.scrollTo(0, 0); }}
              className="card-surface p-5 text-left hover:border-gold/30 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center mb-3"><Icon size={20} className="text-gold" strokeWidth={1.5} /></div>
              <h3 className="font-display text-base mb-1 group-hover:text-gold transition-colors">{it.title}</h3>
              <p className="text-white/45 text-sm line-clamp-2">{it.summary}</p>
            </button>
          );
        })}
      </div>
      {showHelp && <HelpModal actions={data.help_actions} onClose={() => setShowHelp(false)} />}
    </Container>
  );
}

function HelpModal({ actions, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-3" data-testid="youth-help-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg card-surface p-6 max-h-[85vh] overflow-y-auto fade-up">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-xl flex items-center gap-2"><LifeBuoy size={22} className="text-gold" /> J'ai besoin d'aide</h2>
          <button onClick={onClose} data-testid="youth-help-close" className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-white/55 text-sm mb-4">Tu n'es pas seul·e. Voici ce que tu peux faire, maintenant :</p>
        <div className="space-y-2">
          {actions.map((a, i) => {
            const Icon = IC[a.icon] || Heart;
            return (
              <div key={i} className="flex gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div className="w-9 h-9 rounded-lg bg-gold/12 flex items-center justify-center shrink-0"><Icon size={18} className="text-gold" strokeWidth={1.5} /></div>
                <div><p className="text-sm text-white font-medium">{a.title}</p><p className="text-white/55 text-sm">{a.text}</p></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
